import React, { useMemo } from 'react';
import { Container, Flex, Text } from '@/ui';
import { getLocalizedText } from '@/utils/helpers';
import { kycApi } from '@/api';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components';
import { COLORS } from '@/utils/static';
import Markdown from 'react-markdown';
import { Alert } from 'antd';

export const UrlLinkButton = ({ adminView, sumSubStatusRes, refetchSumSubRes }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const isDisabled = useMemo(
    () =>
      sumSubStatusRes?.data.myStatus === 'In process' ||
      sumSubStatusRes?.data.myStatus === 'Accepted' ||
      adminView,
    [sumSubStatusRes]
  );

  const onOpen = async () => {
    try {
      const verificationQuestionnaire = await kycApi.getSumSubQuestionnaire();

      if (verificationQuestionnaire && verificationQuestionnaire.data) {
        const veriffLink = await kycApi.getVeriffLink(verificationQuestionnaire.data[0].id);
        if (veriffLink && veriffLink.data) window.open(veriffLink.data.url, '_blank', 'noreferrer');
        await refetchSumSubRes();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const text =
    "You are on the identity verification page via documents. Please carefully review the document requirements.\n\n **Before you begin:**\n\n- Disable VPN — verification does not work through VPN\n\n- Have your passport or ID ready\n\n- Make sure your phone camera is working\n\n**What will happen:**\n\n1. Click **'Go to Veriff'** — a new tab will open\n\n2. Scan the QR code with your phone camera or a QR code reader app, or enter your phone number to receive a line\n\n3. On your phone: follow the instructions to photograph your document and take a selfie\n\n4. Verification takes 1–3 minutes\n\n**After completing verification:**\n\n1. Close the Veriff tab\n\n2. Return to the tab with the questionnaire\n\n3. Click the **'Back'** button\n\n4. You will see the updated verification status on the verification card\n\n";
  const textRU =
    "Вы находитесь на странице верификации личности через документы. Пожалуйста, внимательно ознакомьтесь с требованиями к документам. \n\n **Перед началом:**\n\n- Отключите VPN — верификация не работает через VPN\n\n- Подготовьте паспорт или удостоверение личности\n\n- Убедитесь, что камера телефона работает\n\n**Что произойдёт:**\n\n1. Нажмите **'Go to Veriff'** — откроется новая вкладка\n\n2. Отсканируйте QR-код камерой телефона или специальным приложением для чтения QR-кодов, либо введите номер телефона для получения ссылкиОтсканируйте QR-код камерой телефона или специальаным приложением для чтения QR-кода или введите номер для получения ссылки\n\n3. На телефоне: сфотографируйте документ и сделайте селфи следуйте инструкциям\n\n4. Проверка занимает 1–3 минуты\n\n**После завершения верификации:**\n\n1. Закройте вкладку Veriff\n\n2. Вернитесь на вкладку с анкетой\n\n3. Нажмите кнопку **'Back'** (Назад)\n\n4. Вы увидите обновлённый статус верификации на карточке верификации\n\n";
  const textLV =
    "Jūs atrodaties personas identifikācijas lapā, izmantojot dokumentus. Lūdzu, rūpīgi iepazīstieties ar dokumentu prasībām.\n\n **Pirms sākat:**\n\n- Izslēdziet VPN — verifikācija nedarbojas caur VPN\n\n- Sagatavojiet pasi vai personu apliecinošu dokumentu\n\n- Pārliecinieties, ka tālruņa kamera darbojas\n\n**Kas notiks:**\n\n1. Nospiediet **'Go to Veriff'** — — atvērsies jauna cilne\n\n2. Noskenējiet QR kodu ar tālruņa kameru vai QR kodu lasīšanas lietotni, vai ievadiet tālruņa numuru, lai saņemtu saiti\n\n3. Tālrunī: sekojiet instrukcijām, lai nofotografētu dokumentu un uzņemtu selfiju\n\n4. Pārbaude aizņem 1–3 minūtes\n\n**Pēc verifikācijas pabeigšanas:**\n\n1. Aizveriet Veriff cilni\n\n2. Atgriezieties cilnē ar anketu\n\n3. Nospiediet pogu **'Back'** (Atpakaļ)\n\n4. Jūs redzēsiet atjaunināto verifikācijas statusu verifikācijas kartītē\n\n";

  return (
    <>
      <Flex $marginTop={12} $marginBottom={25} vertical>
        <Text style={{ fontWeight: '700', marginBottom: '35px' }}>
          Status: <span style={{ color: COLORS.aragonBlue }}>{sumSubStatusRes?.data.myStatus}</span>
        </Text>
        <div className='markdown small' style={{ fontSize: '15px', marginBottom: '25px' }}>
          <Markdown>
            {getLocalizedText(
              {
                text: text,
                text_RU: textRU,
                text_LV: textLV
              },
              'text',
              i18n
            )}
          </Markdown>
          <span>
            {t('If your verification is not confirmed within 5 minutes, please contact us at')}{' '}
            <a href='tel:+371 678 85 886'>+371 678 85 886</a>{' '}
            <a href='mailto:office@spiritcapital.eu'>office@spiritcapital.eu</a>{' '}
            {t('and describe the issue')}.
          </span>
        </div>
        <Alert
          title={t('Important')}
          description={t(
            'After completing verification, Veriff will redirect you to their website — this is normal. Simply close that tab and return to us.'
          )}
          type='info'
          showIcon
        />
      </Flex>
      <Container $marginBottom={60}>
        <Button text={t('Go to Veriff')} onClick={onOpen} disabled={isDisabled} width={170} />
      </Container>
    </>
  );
};
